<template>
  <div class="fixed bottom-4 right-4 z-50 space-y-2" style="min-width: 250px;">
    <transition-group 
      name="toast" 
      tag="div"
      class="space-y-2"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="[
          'max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto overflow-hidden',
          'transform transition-all duration-300 ease-in-out',
          getToastBorderClass(toast.type)
        ]"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <!-- Success Icon -->
              <svg v-if="toast.type === 'success'" class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              
              <!-- Error Icon -->
              <svg v-else-if="toast.type === 'error'" class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              
              <!-- Warning Icon -->
              <svg v-else-if="toast.type === 'warning'" class="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              
              <!-- Info Icon -->
              <svg v-else class="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <div class="ml-3 w-0 flex-1">
              <p v-if="toast.title" :class="['text-sm font-medium', getTitleClass(toast.type)]">
                {{ toast.title }}
              </p>
              <p :class="['text-sm', toast.title ? 'mt-1' : '', getMessageClass(toast.type)]">
                {{ toast.message }}
              </p>
            </div>
            
            <div class="ml-4 flex-shrink-0 flex">
              <button
                @click="removeToast(toast.id)"
                class="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition ease-in-out duration-150"
              >
                <svg class="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Progress bar for auto-dismiss -->
        <div v-if="toast.autoDismiss" class="h-1 bg-gray-200">
          <div 
            :class="['h-1 transition-all ease-linear', getProgressClass(toast.type)]"
            :style="{ width: `${toast.progress}%` }"
          ></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'ToastNotification',
  data() {
    return {
      toasts: []
    }
  },
  methods: {
    showToast({ type = 'info', title = '', message = '', duration = 4000, autoDismiss = true } = {}) {
      const id = Date.now() + Math.random()
      const toast = {
        id,
        type,
        title,
        message,
        autoDismiss,
        progress: 100
      }
      
      this.toasts.push(toast)
      
      if (autoDismiss && duration > 0) {
        // Animate progress bar
        const startTime = Date.now()
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime
          const progress = Math.max(0, 100 - (elapsed / duration) * 100)
          toast.progress = progress
          
          if (progress <= 0) {
            clearInterval(progressInterval)
            this.removeToast(id)
          }
        }, 50)
      }
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id)
      if (index > -1) {
        this.toasts.splice(index, 1)
      }
    },
    
    clearAll() {
      this.toasts = []
    },
    
    // Utility methods for styling
    getToastBorderClass(type) {
      const classes = {
        success: 'border-l-4 border-green-400',
        error: 'border-l-4 border-red-400',
        warning: 'border-l-4 border-yellow-400',
        info: 'border-l-4 border-blue-400'
      }
      return classes[type] || classes.info
    },
    
    getTitleClass(type) {
      const classes = {
        success: 'text-green-800',
        error: 'text-red-800',
        warning: 'text-yellow-800',
        info: 'text-blue-800'
      }
      return classes[type] || classes.info
    },
    
    getMessageClass(type) {
      const classes = {
        success: 'text-green-700',
        error: 'text-red-700',
        warning: 'text-yellow-700',
        info: 'text-blue-700'
      }
      return classes[type] || classes.info
    },
    
    getProgressClass(type) {
      const classes = {
        success: 'bg-green-400',
        error: 'bg-red-400',
        warning: 'bg-yellow-400',
        info: 'bg-blue-400'
      }
      return classes[type] || classes.info
    }
  },
  
  mounted() {
    // Make the toast methods available globally
    this.$root.$toast = {
      success: (message, title = 'Success') => this.showToast({ type: 'success', title, message }),
      error: (message, title = 'Error') => this.showToast({ type: 'error', title, message, duration: 6000 }),
      warning: (message, title = 'Warning') => this.showToast({ type: 'warning', title, message, duration: 5000 }),
      info: (message, title = 'Info') => this.showToast({ type: 'info', title, message }),
      custom: (options) => this.showToast(options)
    }
  }
}
</script>

<style scoped>
/* Toast animations */
.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>
